import React, { useState } from "react";
import axios from "axios";
import { Send, Loader2, CheckCircle2, Phone, Instagram, Facebook } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function ContactForm() {
  const { t, lang } = useLanguage();
  const services = t("services.items");
  const cities = t("area.cities");

  const [form, setForm] = useState({
    name: "",
    email: "",
    service: "",
    location: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | loading | success | error

  const update = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = t("contact.required");
    if (!form.email.trim()) next.email = t("contact.required");
    else if (!EMAIL_RE.test(form.email.trim())) next.email = t("contact.invalid_email");
    if (!form.service) next.service = t("contact.required");
    if (!form.location) next.location = t("contact.required");
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setStatus("loading");
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        service: form.service,
        location: form.location,
        message: form.message.trim(),
        language: lang,
      });
      setStatus("success");
      toast.success(t("contact.success_title"), {
        description: t("contact.success_desc"),
      });
      setForm({ name: "", email: "", service: "", location: "", message: "" });
    } catch (err) {
      setStatus("error");
      toast.error(t("contact.error_title"), {
        description: t("contact.error_desc"),
      });
    }
  };

  if (status === "success") {
    return (
      <section
        id="contact"
        data-testid="contact-section"
        className="relative py-24 md:py-32 bg-brand-aegean"
      >
        <div className="mx-auto max-w-3xl px-5 sm:px-8 text-center">
          <div
            data-testid="contact-success-card"
            className="bg-white/10 border border-white/15 backdrop-blur-md rounded-3xl p-10 md:p-14 text-white"
          >
            <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-brand-terracotta">
              <CheckCircle2 size={28} />
            </div>
            <h2 className="mt-6 font-display text-3xl md:text-4xl tracking-tight">
              {t("contact.success_title")}
            </h2>
            <p className="mt-4 text-white/80 text-base md:text-lg leading-relaxed">
              {t("contact.success_desc")}
            </p>
            <Button
              data-testid="contact-reset-button"
              onClick={() => setStatus("idle")}
              className="mt-8 rounded-full bg-brand-terracotta hover:bg-brand-terracottaHover text-white h-11 px-6"
            >
              {t("nav.cta")}
            </Button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-32 bg-brand-aegean grain"
    >
      <div className="mx-auto max-w-6xl px-5 sm:px-8 grid lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5 text-white">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-terracotta">
            {t("contact.eyebrow")}
          </p>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight">
            {t("contact.title")}
          </h2>
          <p className="mt-5 text-base sm:text-lg text-white/80 leading-relaxed">
            {t("contact.subtitle")}
          </p>

          <div className="mt-10 space-y-4 text-white/85">
            <a
              href="tel:+34645602028"
              data-testid="contact-phone-link"
              className="group flex items-center gap-3 rounded-2xl bg-white/10 border border-white/15 px-4 py-3.5 hover:bg-white/15 transition-colors"
            >
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-brand-terracotta text-white shrink-0">
                <Phone size={18} />
              </span>
              <span className="flex flex-col">
                <span className="text-xs uppercase tracking-widest text-white/60">Telefon · WhatsApp</span>
                <span className="font-display text-lg font-semibold text-white">+34 645 602 028</span>
              </span>
            </a>

            <div className="flex items-start gap-3">
              <span className="mt-1 h-1.5 w-1.5 rounded-full bg-brand-terracotta" />
              <div className="text-sm">Torrevieja · Orihuela Costa · Guardamar · La Marina</div>
            </div>
            <div className="flex items-start gap-3">
              <span className="mt-1 h-1.5 w-1.5 rounded-full bg-brand-terracotta" />
              <div className="text-sm">Deutsch · Español · English</div>
            </div>

            <div className="pt-2 flex items-center gap-3">
              <a
                href="https://www.instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                data-testid="social-instagram"
                className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-white/10 border border-white/15 text-white hover:bg-brand-terracotta hover:border-brand-terracotta transition-colors"
              >
                <Instagram size={19} />
              </a>
              <a
                href="https://www.facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                data-testid="social-facebook"
                className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-white/10 border border-white/15 text-white hover:bg-brand-terracotta hover:border-brand-terracotta transition-colors"
              >
                <Facebook size={19} />
              </a>
              <a
                href="https://www.google.com/maps"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Google"
                data-testid="social-google"
                className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-white/10 border border-white/15 text-white hover:bg-brand-terracotta hover:border-brand-terracotta transition-colors"
              >
                <svg width="19" height="19" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                  <path d="M12.24 10.285V14.4h6.806c-.275 1.765-2.056 5.174-6.806 5.174-4.095 0-7.439-3.389-7.439-7.574s3.344-7.574 7.439-7.574c2.33 0 3.891.989 4.785 1.849l3.254-3.138C18.189 1.186 15.479 0 12.24 0 5.48 0 0 5.48 0 12.24s5.48 12.24 12.24 12.24c7.065 0 11.744-4.967 11.744-11.955 0-.803-.086-1.415-.191-2.024H12.24z"/>
                </svg>
              </a>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <form
            data-testid="contact-form"
            onSubmit={onSubmit}
            noValidate
            className="bg-white rounded-3xl p-6 sm:p-8 md:p-10 shadow-2xl shadow-black/10 border border-white/10"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="name" className="text-brand-ink font-medium">
                  {t("contact.name")}
                </Label>
                <Input
                  id="name"
                  data-testid="contact-name-input"
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  placeholder={t("contact.name_ph")}
                  className="mt-2 h-11 rounded-xl border-brand-sand bg-white focus-visible:ring-brand-terracotta"
                />
                {errors.name && (
                  <p data-testid="error-name" className="mt-1.5 text-xs text-red-600">
                    {errors.name}
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="email" className="text-brand-ink font-medium">
                  {t("contact.email")}
                </Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="contact-email-input"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  placeholder={t("contact.email_ph")}
                  className="mt-2 h-11 rounded-xl border-brand-sand bg-white focus-visible:ring-brand-terracotta"
                />
                {errors.email && (
                  <p data-testid="error-email" className="mt-1.5 text-xs text-red-600">
                    {errors.email}
                  </p>
                )}
              </div>

              <div>
                <Label className="text-brand-ink font-medium">{t("contact.service")}</Label>
                <Select
                  value={form.service}
                  onValueChange={(v) => update("service", v)}
                >
                  <SelectTrigger
                    data-testid="contact-service-select"
                    className="mt-2 h-11 rounded-xl border-brand-sand bg-white focus:ring-brand-terracotta"
                  >
                    <SelectValue placeholder={t("contact.service_ph")} />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.isArray(services) &&
                      services.map((s, i) => (
                        <SelectItem
                          key={i}
                          value={s.title}
                          data-testid={`service-option-${i}`}
                        >
                          {s.title}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                {errors.service && (
                  <p data-testid="error-service" className="mt-1.5 text-xs text-red-600">
                    {errors.service}
                  </p>
                )}
              </div>

              <div>
                <Label className="text-brand-ink font-medium">{t("contact.location")}</Label>
                <Select
                  value={form.location}
                  onValueChange={(v) => update("location", v)}
                >
                  <SelectTrigger
                    data-testid="contact-location-select"
                    className="mt-2 h-11 rounded-xl border-brand-sand bg-white focus:ring-brand-terracotta"
                  >
                    <SelectValue placeholder={t("contact.location_ph")} />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.isArray(cities) &&
                      cities.map((c, i) => (
                        <SelectItem
                          key={c}
                          value={c}
                          data-testid={`location-option-${i}`}
                        >
                          {c}
                        </SelectItem>
                      ))}
                    <SelectItem value="other" data-testid="location-option-other">
                      {t("contact.other_location")}
                    </SelectItem>
                  </SelectContent>
                </Select>
                {errors.location && (
                  <p data-testid="error-location" className="mt-1.5 text-xs text-red-600">
                    {errors.location}
                  </p>
                )}
              </div>
            </div>

            <div className="mt-5">
              <Label htmlFor="message" className="text-brand-ink font-medium">
                {t("contact.message")}
              </Label>
              <Textarea
                id="message"
                data-testid="contact-message-input"
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
                placeholder={t("contact.message_ph")}
                rows={4}
                className="mt-2 rounded-xl border-brand-sand bg-white focus-visible:ring-brand-terracotta"
              />
            </div>

            <Button
              type="submit"
              data-testid="contact-submit-button"
              disabled={status === "loading"}
              className="mt-6 w-full sm:w-auto rounded-full bg-brand-terracotta hover:bg-brand-terracottaHover text-white h-12 px-8 text-base shadow-md"
            >
              {status === "loading" ? (
                <>
                  <Loader2 className="animate-spin" size={18} />
                  {t("contact.submitting")}
                </>
              ) : (
                <>
                  <Send size={18} />
                  {t("contact.submit")}
                </>
              )}
            </Button>
          </form>
        </div>
      </div>
    </section>
  );
}
